import { supabase } from './supabaseClient';
import { FocusSession } from '../types';
import { logError, logInfo } from './logger';
import { getLocalDateOnlyFromValue, getTodayLocalDateOnly, toLocalDateOnly } from './date';

const STORAGE_KEY_SESSIONS = (userId: string) => `dopamind_focus_sessions_${userId}`;

type FocusSessionRow = {
  id: string;
  user_id: string;
  duration: number;
  xp_earned: number;
  started_at: string;
  completed_at: string;
  type: 'focus' | 'break';
  source_session_key?: string | null;
};

const buildSourceSessionKey = (
  userId: string,
  session: Pick<FocusSession, 'startedAt' | 'completedAt' | 'type' | 'duration'>
) => `${userId}:${session.startedAt}:${session.completedAt}:${session.type}:${session.duration}`;

const mapRowToFocusSession = (row: FocusSessionRow): FocusSession => ({
  id: row.id,
  userId: row.user_id,
  duration: row.duration,
  xpEarned: row.xp_earned,
  startedAt: row.started_at,
  completedAt: row.completed_at,
  type: row.type,
  sourceSessionKey: row.source_session_key || undefined
});

const readLocalFocusSessions = (userId: string): FocusSession[] => {
  if (typeof window === 'undefined') return [];

  const stored = window.localStorage.getItem(STORAGE_KEY_SESSIONS(userId));
  if (!stored) return [];

  try {
    const sessions: FocusSession[] = JSON.parse(stored);
    return sessions.sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
  } catch (error) {
    logError('focus', 'Falha ao ler cache local de sessoes.', error);
    return [];
  }
};

const writeLocalFocusSessions = (userId: string, sessions: FocusSession[]) => {
  if (typeof window === 'undefined') return;

  try {
    const trimmed = sessions
      .sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime())
      .slice(0, 200);

    window.localStorage.setItem(STORAGE_KEY_SESSIONS(userId), JSON.stringify(trimmed));
  } catch (error) {
    logError('focus', 'Falha ao salvar cache local de sessoes.', error);
  }
};

const upsertFocusSessionsInDatabase = async (sessions: FocusSession[]): Promise<boolean> => {
  if (!sessions.length) {
    return true;
  }

  const payload = sessions.map((session) => ({
    user_id: session.userId,
    duration: session.duration,
    xp_earned: session.xpEarned,
    started_at: session.startedAt,
    completed_at: session.completedAt,
    type: session.type,
    source_session_key: session.sourceSessionKey || buildSourceSessionKey(session.userId, session)
  }));

  const { error } = await supabase
    .from('focus_sessions')
    .upsert(payload, { onConflict: 'source_session_key' });

  if (error) {
    logError('focus', 'Falha ao sincronizar sessoes no Supabase.', error);
    return false;
  }

  return true;
};

export const saveFocusSession = async (userId: string, session: Omit<FocusSession, 'id'>): Promise<FocusSession> => {
  const sourceSessionKey = session.sourceSessionKey || buildSourceSessionKey(userId, session);
  const localSession: FocusSession = {
    ...session,
    id: sourceSessionKey,
    sourceSessionKey
  };

  const localSessions = readLocalFocusSessions(userId);
  const mergedLocalSessions = [localSession, ...localSessions.filter((item) => item.sourceSessionKey !== sourceSessionKey)];
  writeLocalFocusSessions(userId, mergedLocalSessions);

  const { data, error } = await supabase
    .from('focus_sessions')
    .upsert({
      user_id: userId,
      duration: session.duration,
      xp_earned: session.xpEarned,
      started_at: session.startedAt,
      completed_at: session.completedAt,
      type: session.type,
      source_session_key: sourceSessionKey
    }, { onConflict: 'source_session_key' })
    .select('*')
    .single();

  if (error || !data) {
    logError('focus', 'Falha ao salvar sessao de foco no Supabase. Mantendo fallback local.', error);
    return localSession;
  }

  const savedSession = mapRowToFocusSession(data as FocusSessionRow);
  const refreshedLocalSessions = [savedSession, ...localSessions.filter((item) => item.sourceSessionKey !== sourceSessionKey)];
  writeLocalFocusSessions(userId, refreshedLocalSessions);
  return savedSession;
};

export const getFocusSessions = async (userId: string, limit?: number): Promise<FocusSession[]> => {
  const query = supabase
    .from('focus_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('completed_at', { ascending: false });

  const { data, error } = limit ? await query.limit(limit) : await query;

  if (error) {
    logError('focus', 'Falha ao buscar sessoes no Supabase. Usando cache local.', error);
    const localSessions = readLocalFocusSessions(userId);
    return limit ? localSessions.slice(0, limit) : localSessions;
  }

  const sessions = ((data as FocusSessionRow[]) || []).map(mapRowToFocusSession);
  writeLocalFocusSessions(userId, sessions);
  return limit ? sessions.slice(0, limit) : sessions;
};

export const getTodayFocusSessions = async (userId: string): Promise<FocusSession[]> => {
  const sessions = await getFocusSessions(userId);
  const today = getTodayLocalDateOnly();

  return sessions.filter((session) => {
    const sessionDate = getLocalDateOnlyFromValue(session.completedAt);
    return sessionDate === today;
  });
};

export const getTotalFocusTime = async (userId: string, days?: number): Promise<number> => {
  const sessions = await getFocusSessions(userId);

  if (!days) {
    return sessions.reduce((total, session) => total + session.duration, 0);
  }

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);

  return sessions
    .filter((session) => new Date(session.completedAt) >= cutoff)
    .reduce((total, session) => total + session.duration, 0);
};

export const calculateFocusXp = (durationInSeconds: number): number => {
  const baseXp = Math.floor(durationInSeconds / 60);

  let bonus = 0;
  if (durationInSeconds >= 90 * 60) {
    bonus = 150;
  } else if (durationInSeconds >= 50 * 60) {
    bonus = 75;
  } else if (durationInSeconds >= 25 * 60) {
    bonus = 25;
  }

  return baseXp + bonus;
};

export const getFocusStreak = async (userId: string): Promise<number> => {
  const sessions = await getFocusSessions(userId);
  if (!sessions.length) return 0;

  let streak = 0;
  const today = new Date();

  for (let i = 0; i < 30; i += 1) {
    const checkDate = new Date(today);
    checkDate.setDate(today.getDate() - i);
    const dateStr = toLocalDateOnly(checkDate);

    const hasSession = sessions.some((session) => {
      const sessionDate = getLocalDateOnlyFromValue(session.completedAt);
      return sessionDate === dateStr && session.duration >= 25 * 60;
    });

    if (hasSession) {
      streak += 1;
    } else if (i > 0) {
      break;
    }
  }

  return streak;
};

export const syncFocusSessionsCache = async (userId: string): Promise<void> => {
  const localSessions = readLocalFocusSessions(userId);

  if (!localSessions.length) {
    return;
  }

  // Comentario em pt-BR: aqui importamos o historico local legado para o banco sem duplicar sessao.
  const synced = await upsertFocusSessionsInDatabase(localSessions.map((session) => ({
    ...session,
    sourceSessionKey: session.sourceSessionKey || buildSourceSessionKey(userId, session)
  })));

  if (!synced) {
    return;
  }

  const remoteSessions = await getFocusSessions(userId, 200);
  writeLocalFocusSessions(userId, remoteSessions);
  logInfo('focus', 'Sessoes de foco sincronizadas com sucesso.', { userId });
};
